import React, { useRef, useEffect } from 'react'
import { cn } from '../../lib/utils'
import type { SlideData } from '../../types'

export interface SlideThumbnailsPanelProps {
  slides: SlideData[]
  currentIndex: number
  onSlideSelect: (index: number) => void
  isOpen: boolean
  onClose: () => void
  className?: string
}

/**
 * SlideThumbnailsPanel Component
 *
 * Displays a slide grid with thumbnails for quick navigation.
 * Can be toggled open/closed with keyboard shortcut (T).
 */
export const SlideThumbnailsPanel: React.FC<SlideThumbnailsPanelProps> = ({
  slides,
  currentIndex,
  onSlideSelect,
  isOpen,
  onClose,
  className,
}) => {
  const panelRef = useRef<HTMLDivElement>(null)

  /**
   * Handle clicking outside to close
   */
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        panelRef.current &&
        !panelRef.current.contains(event.target as Node)
      ) {
        onClose()
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
    return undefined
  }, [isOpen, onClose])

  /**
   * Handle keyboard navigation within thumbnails
   */
  const handleThumbnailKeyDown = (
    event: React.KeyboardEvent,
    index: number
  ) => {
    switch (event.key) {
      case 'Enter':
      case ' ':
        event.preventDefault()
        onSlideSelect(index)
        onClose()
        break
      case 'ArrowRight':
        event.preventDefault()
        if (index < slides.length - 1) {
          const nextThumbnail = document.querySelector(
            `[data-slide-index="${index + 1}"]`
          ) as HTMLElement
          nextThumbnail?.focus()
        }
        break
      case 'ArrowLeft':
        event.preventDefault()
        if (index > 0) {
          const prevThumbnail = document.querySelector(
            `[data-slide-index="${index - 1}"]`
          ) as HTMLElement
          prevThumbnail?.focus()
        }
        break
      case 'Escape':
        event.preventDefault()
        onClose()
        break
    }
  }

  if (!isOpen) return null

  return (
    <div
      className={cn(
        'fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4',
        className
      )}
    >
      <div
        ref={panelRef}
        className="bg-card rounded-xl shadow-2xl max-w-4xl w-full max-h-[80vh] overflow-hidden flex flex-col"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <div>
            <h2 className="text-lg font-semibold text-foreground">
              Slide Overview
            </h2>
            <p className="text-sm text-muted-foreground">
              {currentIndex + 1} of {slides.length} slides
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-muted rounded-lg transition-colors"
            aria-label="Close thumbnails"
          >
            <svg
              className="w-5 h-5 text-muted-foreground"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Thumbnails Grid */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
            {slides.map((slide, index) => {
              const isActive = index === currentIndex
              const isPast = index < currentIndex

              return (
                <button
                  key={slide.id || index}
                  data-slide-index={index}
                  onClick={() => {
                    onSlideSelect(index)
                    onClose()
                  }}
                  onKeyDown={(e) => handleThumbnailKeyDown(e, index)}
                  className={cn(
                    'group relative aspect-video rounded-lg overflow-hidden',
                    'border-2 transition-all duration-200',
                    'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2',
                    isActive
                      ? 'border-primary-500 ring-2 ring-primary-500/20'
                      : isPast
                      ? 'border-border opacity-60'
                      : 'border-border hover:border-border'
                  )}
                  aria-label={`Go to slide ${index + 1}: ${slide.title || 'Untitled'}`}
                  aria-current={isActive ? 'true' : undefined}
                >
                  {/* Slide Preview */}
                  <div className="w-full h-full bg-card p-2">
                    <div className="w-full h-full flex items-center justify-center">
                      {slide.title ? (
                        <div className="text-center">
                          <div className="text-[10px] font-bold text-foreground line-clamp-2">
                            {slide.title}
                          </div>
                        </div>
                      ) : (
                        <div className="text-[10px] text-muted-foreground">
                          Slide {index + 1}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Slide Number Badge */}
                  <div
                    className={cn(
                      'absolute bottom-1 left-1 px-1.5 py-0.5 rounded text-xs font-medium',
                      isActive
                        ? 'bg-primary-500 text-white'
                        : 'bg-foreground/70 text-white'
                    )}
                  >
                    {index + 1}
                  </div>

                  {/* Current Slide Indicator */}
                  {isActive && (
                    <div className="absolute top-1 right-1">
                      <div className="w-2 h-2 bg-primary-500 rounded-full animate-pulse" />
                    </div>
                  )}

                  {/* Notes Indicator */}
                  {slide.notes && (
                    <div className="absolute top-1 left-1">
                      <svg
                        className="w-3 h-3 text-amber-500"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" />
                      </svg>
                    </div>
                  )}
                </button>
              )
            })}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-3 bg-muted border-t border-border flex items-center justify-between text-xs text-muted-foreground">
          <span>Press T to toggle</span>
          <span>Use arrow keys to navigate, Enter to select</span>
        </div>
      </div>
    </div>
  )
}

export default SlideThumbnailsPanel
